import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import { cn } from 'cn';
import { type PropsWithChildren, useId } from 'react';

import { isFieldRequired, unique } from '@vestigas/utils';

import { ValidationError } from '@vestigas/ui/molecules';

import { useFieldContext } from './form-context';
import { InputLabel } from './InputLabel';

type P = PropsWithChildren<{
  readonly buttonsClassName?: string;
  readonly className?: string;
  readonly hint?: string;
  readonly isDisabled?: boolean;
  readonly isExclusive?: boolean;
  readonly isNullable?: boolean;
  readonly label?: string;
  readonly options?: Array<{ label: string; value: string | boolean }>;
  readonly testId?: string;
}> &
  ComponentStyling;

export const ToggleButtonField = ({
  buttonsClassName,
  children,
  className,
  hint,
  isDisabled = false,
  isExclusive = false,
  isNullable = false,
  label,
  options,
  style,
  testId,
}: P) => {
  const field = useFieldContext();

  const isRequired = isFieldRequired(
    field.options.form.options.validators,
    field.name,
  );

  const hasErrors = Boolean(field.state.meta.errors?.length);

  const id = useId();

  return (
    <div className={cn('relative', className)} style={style}>
      {Boolean(label) && (
        <InputLabel hint={hint} htmlFor={id} isRequired={isRequired}>
          {label}
        </InputLabel>
      )}
      <ToggleButtonGroup
        className={cn('h-10 bg-white', className)}
        disabled={isDisabled}
        exclusive={isExclusive}
        id={id}
        size="small"
        value={field.state.value}
        onChange={(_, value) => {
          if (isNullable) {
            field.handleChange(value);
            return;
          }

          if (value !== null) {
            // Enforce one option to always be selected
            field.handleChange(value);
          }
        }}
      >
        {children ??
          options?.map(({ label, value }) => (
            <ToggleButton
              key={value}
              className={cn('px-2', buttonsClassName)}
              value={value}
            >
              {label}
            </ToggleButton>
          ))}
      </ToggleButtonGroup>
      {hasErrors ? (
        <ValidationError>
          {unique(field.state.meta.errors.map(({ message }) => message)).join(
            ', ',
          )}
        </ValidationError>
      ) : null}
    </div>
  );
};
